import * as React from 'react'
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from 'embla-carousel-react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import { cn } from '@shared/ui/lib/utils'
import { Button } from './button'
import type { ButtonProps } from './button'
import './carousel.css'

type CarouselApi = UseEmblaCarouselType[1]
type CarouselOptions = Parameters<typeof useEmblaCarousel>[0]
type CarouselPlugin = Parameters<typeof useEmblaCarousel>[1]
type CarouselOrientation = 'horizontal' | 'vertical'

interface CarouselContextValue {
  readonly api: CarouselApi
  readonly canScrollNext: boolean
  readonly canScrollPrev: boolean
  readonly carouselRef: UseEmblaCarouselType[0]
  readonly orientation: CarouselOrientation
  readonly scrollNext: () => void
  readonly scrollPrev: () => void
}

interface CarouselProps extends React.ComponentProps<'div'> {
  readonly opts?: CarouselOptions
  readonly orientation?: CarouselOrientation
  readonly plugins?: CarouselPlugin
  readonly setApi?: (api: CarouselApi) => void
}

const CarouselContext = React.createContext<CarouselContextValue | null>(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />')
  }

  return context
}

function Carousel({
  children,
  className,
  onKeyDown,
  opts,
  orientation = 'horizontal',
  plugins,
  setApi,
  ...props
}: CarouselProps) {
  const axis = orientation === 'horizontal' ? 'x' : 'y'
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis,
    },
    plugins,
  )
  const [canScrollPrev, setCanScrollPrev] = React.useState(false)
  const [canScrollNext, setCanScrollNext] = React.useState(false)

  const onSelect = React.useCallback((selectedApi: NonNullable<CarouselApi>) => {
    setCanScrollPrev(selectedApi.canScrollPrev())
    setCanScrollNext(selectedApi.canScrollNext())
  }, [])

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev()
  }, [api])

  const scrollNext = React.useCallback(() => {
    api?.scrollNext()
  }, [api])

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(event)

      if (event.defaultPrevented) return

      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        scrollPrev()
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault()
        scrollNext()
      }
    },
    [onKeyDown, scrollNext, scrollPrev],
  )

  React.useEffect(() => {
    if (!api || !setApi) return

    setApi(api)
  }, [api, setApi])

  React.useEffect(() => {
    if (!api) return

    const updateCanScroll = () => onSelect(api)
    const animationFrame = window.requestAnimationFrame(updateCanScroll)

    api.on('reInit', updateCanScroll)
    api.on('select', updateCanScroll)

    return () => {
      window.cancelAnimationFrame(animationFrame)
      api.off('select', updateCanScroll)
      api.off('reInit', updateCanScroll)
    }
  }, [api, onSelect])

  const contextValue = React.useMemo<CarouselContextValue>(
    () => ({
      api,
      canScrollNext,
      canScrollPrev,
      carouselRef,
      orientation,
      scrollNext,
      scrollPrev,
    }),
    [
      api,
      canScrollNext,
      canScrollPrev,
      carouselRef,
      orientation,
      scrollNext,
      scrollPrev,
    ],
  )

  return (
    <CarouselContext.Provider value={contextValue}>
      <div
        aria-roledescription="carousel"
        className={cn('relative', className)}
        data-orientation={orientation}
        data-slot="carousel"
        onKeyDownCapture={handleKeyDown}
        role="region"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  )
}

function CarouselContent({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  const { carouselRef, orientation } = useCarousel()

  return (
    <div data-slot="carousel-viewport" ref={carouselRef}>
      <div
        className={cn(
          'flex',
          orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
          className,
        )}
        data-slot="carousel-content"
        {...props}
      />
    </div>
  )
}

function CarouselItem({ className, ...props }: React.ComponentProps<'div'>) {
  const { orientation } = useCarousel()

  return (
    <div
      aria-roledescription="slide"
      className={cn(
        'min-w-0 shrink-0 grow-0 basis-full',
        orientation === 'horizontal' ? 'pl-4' : 'pt-4',
        className,
      )}
      data-slot="carousel-item"
      role="group"
      {...props}
    />
  )
}

function CarouselPrevious({
  children,
  className,
  disabled,
  onClick,
  size = 'icon-sm',
  type = 'button',
  variant = 'outline',
  ...props
}: ButtonProps) {
  const { canScrollPrev, orientation, scrollPrev } = useCarousel()

  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    onClick?.(event)

    if (event.defaultPrevented) return

    scrollPrev()
  }

  const label =
    typeof props['aria-label'] === 'string' ? props['aria-label'] : 'Previous slide'

  return (
    <Button
      aria-label={label}
      className={cn(
        'absolute rounded-full',
        orientation === 'horizontal'
          ? 'left-2 top-1/2 -translate-y-1/2'
          : 'left-1/2 top-2 -translate-x-1/2 rotate-90',
        className,
      )}
      data-carousel-control="true"
      disabled={disabled || !canScrollPrev}
      onClick={handleClick}
      size={size}
      type={type}
      variant={variant}
      {...props}
    >
      {children ?? <ChevronLeft aria-hidden="true" />}
      <span className="sr-only">{label}</span>
    </Button>
  )
}

function CarouselNext({
  children,
  className,
  disabled,
  onClick,
  size = 'icon-sm',
  type = 'button',
  variant = 'outline',
  ...props
}: ButtonProps) {
  const { canScrollNext, orientation, scrollNext } = useCarousel()

  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    onClick?.(event)

    if (event.defaultPrevented) return

    scrollNext()
  }

  const label =
    typeof props['aria-label'] === 'string' ? props['aria-label'] : 'Next slide'

  return (
    <Button
      aria-label={label}
      className={cn(
        'absolute rounded-full',
        orientation === 'horizontal'
          ? 'right-2 top-1/2 -translate-y-1/2'
          : 'bottom-2 left-1/2 -translate-x-1/2 rotate-90',
        className,
      )}
      data-carousel-control="true"
      disabled={disabled || !canScrollNext}
      onClick={handleClick}
      size={size}
      type={type}
      variant={variant}
      {...props}
    >
      {children ?? <ChevronRight aria-hidden="true" />}
      <span className="sr-only">{label}</span>
    </Button>
  )
}

export type {
  CarouselApi,
  CarouselOptions,
  CarouselOrientation,
  CarouselPlugin,
}
export {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
}

